/**
 * Per-tile indicator and Pine runs.
 *
 * This is what makes a tile a real chart rather than a candlestick view:
 * each tile holds its own list of indicator instance ids and (optionally) a
 * saved Pine script id, and this hook turns both into the overlay groups,
 * panes, shapes, labels and tables the Chart component already knows how to
 * render.
 *
 * The indicator catalogue and the user's saved scripts are both small and
 * change rarely, so they are fetched once per workspace rather than per
 * tile. Each tile's own run is per (tile, symbol, timeframe, config), so
 * four tiles on four symbols each get their own four requests.
 */
import type {
  IndicatorInstance,
  IndicatorRun,
  PineRunResponse,
} from "@tt/shared";
import { useMemo } from "react";

import type { OverlayGroup, PaneSpec, SeriesSpec } from "../components/Chart";
import { useAsync } from "../hooks/useApi";
import { api } from "../lib/api";
import type { Tile } from "./workspace";

interface Script {
  id: number;
  name: string;
  source: string;
  /** "strategy" or "indicator"; TV-style pickers show them in separate tabs. */
  kind: string;
}

export interface CatalogueIndicator {
  id: string;
  name: string;
  short: string;
  category: string;
  pane: "overlay" | "separate";
  /** Default parameter values, keyed by param name. Used when a user adds an
   *  indicator from the workspace picker and we need an instance on the fly. */
  defaults: Record<string, unknown>;
}

export interface TileCatalogue {
  /** By-id lookup for runtime (resolving an instance's def). */
  indicators: Map<string, CatalogueIndicator>;
  /** Original list order, for picker UIs. */
  list: CatalogueIndicator[];
  scripts: Script[];
}

/** Loaded once per workspace, shared by every tile. */
export function useTileCatalogue(): TileCatalogue {
  const cat = useAsync((s) => api.indicatorCatalogue(s), []);
  const scripts = useAsync((s) => api.scripts(s), []);

  return useMemo<TileCatalogue>(() => {
    const indicators = new Map<string, CatalogueIndicator>();
    const list: CatalogueIndicator[] = [];
    for (const d of cat.data?.indicators ?? []) {
      const defaults: Record<string, unknown> = {};
      for (const p of d.params) defaults[p.name] = p.default;
      const entry: CatalogueIndicator = {
        id: d.id, name: d.name, short: d.short,
        category: d.category, pane: d.pane, defaults,
      };
      indicators.set(d.id, entry);
      list.push(entry);
    }
    const scriptList: Script[] = (scripts.data?.scripts ?? []).map((s) => ({
      id: s.id, name: s.name, source: s.source,
      kind: typeof s.kind === "string" ? s.kind : "indicator",
    }));
    return { indicators, list, scripts: scriptList };
  }, [cat.data, scripts.data]);
}

/** Everything one tile wants to pass to Chart, including its data fetch. */
export interface TileRun {
  loading: boolean;
  overlayGroups: OverlayGroup[];
  panes: PaneSpec[];
  shapes: SeriesSpec[];
  labels: PineRunResponse["labels"];
  tables: PineRunResponse["tables"];
  /** Pine drawing primitives: rendered via <PineDrawings> inside the chart's
   *  price-pane overlay, not as top-level Chart props. */
  lines: PineRunResponse["lines"];
  boxes: PineRunResponse["boxes"];
  polylines: PineRunResponse["polylines"];
  linefills: PineRunResponse["linefills"];
  pineError: string | null;
}

/** Build everything the Chart on a tile needs: indicator run + Pine run. */
export function useTileRun(tile: Tile, cat: TileCatalogue): TileRun {
  // The user's indicator-instance definitions live in localStorage and are
  // shared with the single-chart view. Resolving a tile's referenced ids
  // against them means a change in one view (an EMA length edit) is visible
  // in both -- which is what users expect.
  const instances = useResolvedInstances(tile.indicatorIds ?? []);
  const instKey = instancesKey(instances);

  const indicatorRun = useAsync<IndicatorRun>(
    (s) =>
      api.runIndicators(
        {
          symbol: tile.symbol, timeframe: tile.timeframe, bars: 500,
          instances: instances.map((i) => ({
            instance_id: i.instance_id,
            indicator_id: i.indicator_id,
            params: i.params,
          })),
        },
        s,
      ),
    [tile.symbol, tile.timeframe, instKey],
    instances.length > 0,
  );

  // Pine: look up the script source and run it when the id or symbol changes.
  const script = cat.scripts.find((s) => s.id === tile.scriptId);
  const pineSource = script?.source ?? "";
  const pineRun = useAsync<PineRunResponse>(
    (s) =>
      api.runPine(
        {
          source: pineSource,
          symbol: tile.symbol, timeframe: tile.timeframe,
          bars: 500, inputs: {},
        },
        s,
      ),
    [tile.symbol, tile.timeframe, tile.scriptId, pineSource],
    !!pineSource,
  );

  return useMemo<TileRun>(() => {
    const overlayGroups: OverlayGroup[] = [];
    const panes: PaneSpec[] = [];
    const shapes: SeriesSpec[] = [];

    // ── built-in indicator overlays ──
    for (const inst of indicatorRun.data?.instances ?? []) {
      const def = cat.indicators.get(inst.indicator_id);
      const pane = inst.pane;
      const series: SeriesSpec[] = inst.plots.map((p) => ({
        key: `${inst.instance_id}:${p.name}`,
        name: p.name,
        values: p.values,
        color: p.color,
        kind: p.kind,
        linewidth: p.linewidth,
      }));
      if (pane === "separate") {
        panes.push({
          id: inst.instance_id,
          title: def?.name ?? inst.indicator_id,
          plots: series,
          levels: inst.levels.map((l) => ({
            value: l.value, color: l.color, style: l.style, label: l.label,
          })),
          scale: inst.scale,
          fills: inst.fills.map((f) => ({
            plot_a: f.plot_a, plot_b: f.plot_b, color: f.color,
          })),
        });
      } else {
        overlayGroups.push({
          id: inst.instance_id,
          title: def?.name ?? inst.indicator_id,
          series,
        });
      }
    }

    // ── Pine overlays, shape plots, labels, tables, lines, boxes ──
    let pineError: string | null = null;
    const pine = pineRun.data;
    const sameSym = pine
      && pine.symbol === tile.symbol
      && pine.timeframe === tile.timeframe;
    if (pine && sameSym) {
      const firstErr = pine.errors[0];
      if (firstErr) {
        pineError = firstErr.message;
      }
      const overlaySeries: SeriesSpec[] = pine.plots
        .filter((p) => p.overlay && p.kind !== "shape")
        .map((p) => ({
          key: `pine:${p.id}`, name: p.title,
          values: p.values, color: p.color ?? "#4d8dff",
          kind: p.kind, linewidth: p.linewidth,
          colors: p.colors ?? null,
        }));
      if (overlaySeries.length > 0) {
        overlayGroups.push({
          id: "__pine_overlay__",
          title: String(pine.declaration.title ?? script?.name ?? "Script"),
          series: overlaySeries,
        });
      }
      const paneSeries = pine.plots.filter((p) => !p.overlay && p.kind !== "shape");
      if (paneSeries.length > 0) {
        panes.push({
          id: "__pine_pane__",
          title: String(pine.declaration.title ?? script?.name ?? "Script"),
          plots: paneSeries.map((p) => ({
            key: `pine:${p.id}`, name: p.title,
            values: p.values, color: p.color ?? "#4d8dff",
            kind: p.kind, linewidth: p.linewidth,
            colors: p.colors ?? null,
          })),
        });
      }
      for (const sp of pine.plots.filter((p) => p.kind === "shape")) {
        shapes.push({
          key: `pine:${sp.id}`, name: sp.title,
          values: sp.values, color: sp.color ?? "#4d8dff",
          kind: "shape", linewidth: 1,
        });
      }
    }

    return {
      loading: indicatorRun.loading || pineRun.loading,
      overlayGroups, panes, shapes,
      labels: sameSym && pine ? pine.labels : [],
      tables: sameSym && pine ? pine.tables : [],
      lines: sameSym && pine ? pine.lines : [],
      boxes: sameSym && pine ? pine.boxes : [],
      polylines: sameSym && pine ? pine.polylines : [],
      linefills: sameSym && pine ? pine.linefills : [],
      pineError,
    };
  }, [indicatorRun.data, indicatorRun.loading, pineRun.data, pineRun.loading,
      tile.symbol, tile.timeframe, cat.indicators, script?.name]);
}

/** Hydrate instance ids into full definitions from the user's saved set.
 *
 *  The ids are what the tile stores; the parameters live in the same
 *  localStorage the single-chart view reads. If a tile references an id that
 *  no longer exists -- the user removed it from the single-chart view -- the
 *  reference is skipped rather than treated as an error.
 */
function useResolvedInstances(ids: string[]): IndicatorInstance[] {
  const key = ids.join("|");
  return useMemo(() => {
    try {
      const raw = localStorage.getItem("tt.indicators");
      if (!raw) return [];
      const all = JSON.parse(raw) as IndicatorInstance[];
      if (!Array.isArray(all)) return [];
      const byId = new Map(all.map((i) => [i.instance_id, i]));
      return ids
        .map((id) => byId.get(id))
        .filter((i): i is IndicatorInstance => !!i);
    } catch {
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

function instancesKey(instances: IndicatorInstance[]): string {
  return instances
    .map((i) => `${i.instance_id}:${i.indicator_id}:${JSON.stringify(i.params)}`)
    .join("|");
}
